import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const KubernetesLogStreamPage = () => {
    const location = useLocation();
    const { selectedAKSCluster } = useSelector((state: RootState) => state.monitoring);

    const [logs, setLogs] = useState<any[]>([]);
    const [isStreaming, setIsStreaming] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);
    const [filterText, setFilterText] = useState<any>('');
    const [logLevel, setLogLevel] = useState<any>('all');
    const logEndRef = useRef<any>(null);

    // Parse query parameters
    const params = new URLSearchParams(location.search);
    const cluster = params.get('cluster');
    const namespace = params.get('namespace');
    const pod = params.get('pod');
    const container = params.get('container');

    // Check if the current pod's cluster matches the selected AKS cluster filter
    const isFilteredOut = selectedAKSCluster && cluster !== selectedAKSCluster.name;

    useEffect(() => {
        if (!cluster || !namespace || !pod) {
            setError('Missing required parameters: cluster, namespace, and pod');
            return;
        }

        // Don't fetch logs if filtered out
        if (isFilteredOut) {
            setLogs([]);
            return;
        }

        const fetchLogs = async () => {
            try {
                let url = `/api/monitoring/logs/${cluster}/${namespace}/${pod}`;
                if (container) {
                    url += `/${container}`;
                }
                url += '?follow=true';

                // Using EventSource for streaming
                const eventSource = new EventSource(url);

                eventSource.onmessage = (event) => {
                    // Parse log lines and convert to structured format if possible
                    const logData = event.data;
                    const logLines = logData.split('\n').filter((line: any) => line.trim());

                    setLogs((prevLogs: any) => [...prevLogs, ...logLines.map((line: any) => {
                        // Try to detect log level
                        let level = 'INFO';
                        if (line.includes(' ERROR ') || line.includes('[ERROR]')) level = 'ERROR';
                        if (line.includes(' WARN ') || line.includes('[WARN]')) level = 'WARN';
                        if (line.includes(' DEBUG ') || line.includes('[DEBUG]')) level = 'DEBUG';

                        return {
                            timestamp: new Date().toISOString(),
                            level,
                            message: line,
                            component: container || 'app'
                        };
                    })]);
                };

                eventSource.onerror = () => {
                    setError('Connection to log stream was lost. Trying to reconnect...');
                    // EventSource will automatically try to reconnect
                };

                return () => {
                    eventSource.close();
                };
            } catch (error: any) {
                setError(`Failed to connect to log stream: ${error.message}`);
            }
        };

        if (isStreaming) {
            fetchLogs();
        }

    }, [cluster, namespace, pod, container, isStreaming, isFilteredOut]);

    // Auto-scroll to bottom when new logs arrive
    useEffect(() => {
        if (logEndRef.current && isStreaming) {
            logEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs, isStreaming]);

    const toggleStreaming = () => {
        setIsStreaming(!isStreaming);
    };

    const clearLogs = () => {
        setLogs([]);
    };

    const downloadLogs = () => {
        const logText = logs.map(log =>
            `${log.timestamp} [${log.level}] ${log.component}: ${log.message}`
        ).join('\n');

        const blob = new Blob([logText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${pod}${container ? `-${container}` : ''}-logs.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const filteredLogs = logs.filter(log => {
        const matchesFilter = log.message.toLowerCase().includes(filterText.toLowerCase());
        const matchesLevel = logLevel === 'all' || log.level === logLevel;
        return matchesFilter && matchesLevel;
    });

    return (
        <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 shadow px-4 py-3 flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-medium text-gray-900 dark:text-white">
                        {pod} {container ? `/ ${container}` : ''} Logs
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {cluster} / {namespace}
                        {selectedAKSCluster && (
                            <span className="ml-2 text-xs">
                                (Filtered by: {selectedAKSCluster.name})
                            </span>
                        )}
                    </p>
                </div>

                <div className="flex items-center space-x-4">
                    <div className="relative">
                        <input
                            type="text"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            placeholder="Filter logs..."
                            className="pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                    </div>

                    <select
                        value={logLevel}
                        onChange={(e) => setLogLevel(e.target.value)}
                        className="pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                        <option value="all">All Levels</option>
                        <option value="INFO">Info</option>
                        <option value="DEBUG">Debug</option>
                        <option value="WARN">Warning</option>
                        <option value="ERROR">Error</option>
                    </select>

                    <button
                        onClick={toggleStreaming}
                        disabled={isFilteredOut??undefined}
                        className={`px-3 py-2 border rounded-md text-sm font-medium ${isFilteredOut
                                ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                                : isStreaming
                                    ? 'border-red-300 text-red-700 bg-red-100 hover:bg-red-200'
                                    : 'border-green-300 text-green-700 bg-green-100 hover:bg-green-200'
                            }`}
                    >
                        {isStreaming ? 'Pause' : 'Resume'}
                    </button>

                    <button
                        onClick={clearLogs}
                        disabled={isFilteredOut??undefined}
                        className={`px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${isFilteredOut
                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                : 'text-gray-700 bg-white hover:bg-gray-50'
                            }`}
                    >
                        Clear
                    </button>

                    <button
                        onClick={downloadLogs}
                        disabled={isFilteredOut || logs.length === 0}
                        className={`px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${isFilteredOut || logs.length === 0
                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                : 'text-gray-700 bg-white hover:bg-gray-50'
                            }`}
                    >
                        Download
                    </button>
                </div>
            </div>

            {/* Filter Warning Banner */}
            {isFilteredOut && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-4 py-3">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                        This pod is in cluster <strong>{cluster}</strong> but you have filtered to <strong>{selectedAKSCluster?.name}</strong>.
                        Select "All Clusters" from the sidebar to view these logs.
                    </p>
                </div>
            )}

            {/* Log content */}
            <div className="flex-1 overflow-auto p-4 font-mono text-sm bg-black text-white">
                {error && !isFilteredOut && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {isFilteredOut ? (
                    <div className="text-gray-500 italic">
                        Logs hidden due to cluster filter. Select "All Clusters" or "{cluster}" from the sidebar.
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="text-gray-500 italic">
                        {isStreaming ? "Waiting for logs..." : "No logs available"}
                    </div>
                ) : (
                    filteredLogs.map((log, index) => (
                        <div
                            key={index}
                            className={`py-1 border-b border-gray-800 ${log.level === 'ERROR' ? 'text-red-400' :
                                    log.level === 'WARN' ? 'text-yellow-400' :
                                        log.level === 'INFO' ? 'text-blue-400' :
                                            'text-gray-400'
                                }`}
                        >
                            <span className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>{' '}
                            <span className="px-1 rounded bg-opacity-20" style={{
                                backgroundColor: log.level === 'ERROR' ? 'rgba(220, 38, 38, 0.2)' :
                                    log.level === 'WARN' ? 'rgba(245, 158, 11, 0.2)' :
                                        log.level === 'INFO' ? 'rgba(59, 130, 246, 0.2)' :
                                            'rgba(156, 163, 175, 0.2)'
                            }}>
                                {log.level}
                            </span>{' '}
                            <span className="text-purple-400">{log.component}:</span>{' '}
                            <span>{log.message}</span>
                        </div>
                    ))
                )}
                <div ref={logEndRef} />
            </div>

            {/* Status bar */}
            <div className="bg-gray-200 dark:bg-gray-800 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 flex justify-between">
                <div>
                    {isFilteredOut ? 'Logs filtered out' : (
                        <>
                            {filteredLogs.length} log entries {filterText && `(filtered from ${logs.length})`}
                        </>
                    )}
                </div>
                <div>
                    {isFilteredOut ? `Cluster filter active: ${selectedAKSCluster?.name}` :
                        isStreaming ? 'Live streaming' : 'Streaming paused'}
                </div>
            </div>
        </div>
    );
};

export default KubernetesLogStreamPage;